import React, { useState, useEffect } from 'react';
import { contactsAPI, clientsAPI } from '../../utils/api';
import { formatApiError } from '../../utils/errorFormatter';
import { X, Loader2 } from 'lucide-react';

const ContactModal = ({ isOpen, onClose, contact = null, onSuccess, preSelectedClientId = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  
  const [formData, setFormData] = useState({
    full_name: '',
    client_id: preSelectedClientId || '',
    designation: '',
    department: '',
    email: '',
    phone: '',
    linkedin: '',
    decision_maker: false,
    influencer_level: 'Influencer',
    is_primary: false,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      if (contact) {
        setFormData({
          full_name: contact.full_name || '',
          client_id: contact.client_id || '',
          designation: contact.designation || '',
          department: contact.department || '',
          email: contact.email || '',
          phone: contact.phone || '',
          linkedin: contact.linkedin || '',
          decision_maker: contact.decision_maker || false,
          influencer_level: contact.influencer_level || 'Influencer',
          is_primary: contact.is_primary || false,
          notes: contact.notes || ''
        });
      } else if (preSelectedClientId) {
        setFormData(prev => ({ ...prev, client_id: preSelectedClientId }));
      }
    }
  }, [isOpen, contact, preSelectedClientId]);

  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getAll();
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.full_name) throw new Error('Full name is required');
      if (!formData.client_id) throw new Error('Client is required');

      if (contact) {
        await contactsAPI.update(contact._id, formData);
      } else {
        await contactsAPI.create(formData);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save contact'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="contact-modal-overlay"
    >
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-modal"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-slate-900">
            {contact ? 'Edit Contact' : 'Create New Contact'}
          </h2>
          <button 
            type="button"
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="contact-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
              <select
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
                disabled={!!preSelectedClientId}
              >
                <option value="">Select Client</option>
                {clients.map(c => (
                  <option key={c._id} value={c._id}>{c.client_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Designation</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">LinkedIn</label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Influencer Level</label>
              <select
                name="influencer_level"
                value={formData.influencer_level}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="Champion">Champion</option>
                <option value="Influencer">Influencer</option>
                <option value="Gatekeeper">Gatekeeper</option>
                <option value="End User">End User</option>
              </select>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="decision_maker"
                  name="decision_maker"
                  checked={formData.decision_maker}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary focus:ring-primary border-slate-300 rounded"
                />
                <label htmlFor="decision_maker" className="ml-2 text-sm text-slate-700">
                  Decision Maker
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_primary"
                  name="is_primary"
                  checked={formData.is_primary}
                  onChange={handleChange}
                  className="w-4 h-4 text-amber-500 focus:ring-amber-500 border-slate-300 rounded"
                  data-testid="is-primary-checkbox"
                />
                <label htmlFor="is_primary" className="ml-2 text-sm text-slate-700">
                  Primary Contact
                </label>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {contact ? 'Update Contact' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;